import type { PricingType } from "@/hooks/usePricingTypes";
import { packagePriceText, packageSummary, type MenuPackage } from "@/lib/menu-packages";

interface MenuPackagesSectionProps {
  packages: MenuPackage[];
  showPrices: boolean;
  pricingTypes: PricingType[] | undefined;
  /** "Get a quote for this package": pre-select it on the quote form. Omitted
   * when the page has no quote form (a plan-wizard visitor, #1221). */
  onQuote?: (packageId: string) => void;
}

/**
 * The "Packages" section on /menu/<slug> (#932). Rows reuse the menu item row
 * markup so packages read like part of the menu, not a separate checkout.
 */
export function MenuPackagesSection({ packages, showPrices, pricingTypes, onQuote }: MenuPackagesSectionProps) {
  return (
    <section id="packages" aria-labelledby="packages-heading">
      <div className="mb-6">
        <h2 id="packages-heading" className="text-2xl font-heading font-bold text-foreground">Packages</h2>
        <p className="text-muted-foreground mt-1 font-secondary">
          One price per guest. You choose the dishes, we build the menu.
        </p>
      </div>
      <div className="divide-y divide-border">
        {packages.map((pkg) => {
          const summary = packageSummary(pkg);
          const price = packagePriceText(pkg, showPrices, pricingTypes);
          return (
            <div key={pkg.id} className="flex items-start justify-between py-4 gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-foreground">{pkg.name}</h3>
                {summary && <p className="text-sm text-muted-foreground mt-1">{summary}</p>}
                {pkg.min_guests != null && (
                  <p className="text-xs text-muted-foreground mt-1">From {pkg.min_guests} guests</p>
                )}
                {onQuote && (
                  <button
                    type="button"
                    onClick={() => onQuote(pkg.id)}
                    className="mt-1 min-h-[44px] text-sm font-medium text-primary underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
                  >
                    Get a quote for this package
                    <span className="sr-only">: {pkg.name}</span>
                  </button>
                )}
              </div>
              {price && (
                <span className="text-right text-sm font-semibold text-foreground whitespace-nowrap">
                  {price.price}
                  {price.unit && (
                    <span className="block text-xs font-normal text-muted-foreground">/ {price.unit}</span>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
